import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Switch, Input, Button, Tag, Tooltip, Icon, message, Skeleton, Alert } from 'antd';
import styled from 'styled-components';

import { request } from '~/_shared/instance';
import { frequency } from '~/_domains/tasks/helpers';
import {
  getProfile,
  taskMatchesProfile,
  describeRule,
  hasRule,
  groupChildren,
  groupApplies,
  groupStatus,
  GROUP_PENDING,
  GROUP_OVER,
} from '~/_domains/tasks/enrollment';

const DEPARTMENTS = {
  1: { name: 'Societário', color: 'blue' },
  2: { name: 'Administrativo', color: 'green' },
  3: { name: 'Contábil', color: 'purple' },
  4: { name: 'Fiscal', color: 'orange' },
  5: { name: 'Pessoal', color: 'cyan' },
  6: { name: 'Sucesso do Cliente', color: 'magenta' },
};

const getDepartment = (id) => DEPARTMENTS[parseInt(id)] || { name: 'Desconhecido', color: 'default' };

// A regra do cliente é escolhida na aba Regra; aqui ela só aparece como
// referência para as marcas "faltando" e "exceção" da lista.
const RuleBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  background: #fafafa;
  font-size: 13px;
  color: #595959;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Counters = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const PendingBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
`;

const PendingText = styled.div`
  font-size: 13px;
  color: #874d00;
`;

const Diff = styled.strong`
  color: ${(props) => (props.tone === 'in' ? '#389e0d' : '#cf1322')};
`;

// A tarifa desligada continua aparecendo quando o cliente ja esta matriculado
// nela — esconder daria a impressao de que a matricula sumiu.
const isVisibleTask = (task, assignedIds) => (
  !task.disabled_at || assignedIds.indexOf(task.id) > -1
);

const CustomerTasks = ({ customer, onOpenRule, onChange }) => {
  const [tasks, setTasks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [baseline, setBaseline] = useState([]);
  const [selected, setSelected] = useState([]);
  const [savedProfileKey, setSavedProfileKey] = useState(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const customerId = (customer && customer.id) || null;

  useEffect(() => {
    let subscribed = true;

    const load = async () => {
      if (!customerId) {
        setTasks([]);
        setBaseline([]);
        setSelected([]);
        return;
      }

      setLoading(true);
      try {
        const response = await request('GET', `/customer/${customerId}/tasks`, null, false);
        if (!subscribed) return;
        const body = response.data || {};
        const list = body.data || [];
        const info = body.customer || null;
        const assigned = list.filter((task) => task.is_assigned).map((task) => task.id);
        const saved = (info && info.enrollment_profile) || undefined;
        setTasks(list);
        setGroups(body.groups || []);
        setBaseline(assigned);
        setSelected(assigned);
        setSavedProfileKey(saved);
      } catch (error) {
        // o interceptor da request ja avisa o usuario
      } finally {
        if (subscribed) setLoading(false);
      }
    };

    load();

    return () => {
      subscribed = false;
    };
  }, [customerId]);

  const visibleTasks = useMemo(
    () => tasks.filter((task) => isVisibleTask(task, baseline)),
    [tasks, baseline]
  );

  const filteredTasks = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return visibleTasks
      .filter((task) => (query ? task.name.toLowerCase().indexOf(query) > -1 : true))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visibleTasks, searchQuery]);

  const toAdd = useMemo(
    () => selected.filter((id) => baseline.indexOf(id) === -1),
    [selected, baseline]
  );

  const toRemove = useMemo(
    () => baseline.filter((id) => selected.indexOf(id) === -1),
    [selected, baseline]
  );

  const dirty = toAdd.length > 0 || toRemove.length > 0;

  const activeProfile = useMemo(() => getProfile(savedProfileKey), [savedProfileKey]);

  // Tarefas que o perfil ativo pede. É o que dá sentido a "exceção" e a
  // "faltando" na lista abaixo.
  const profileTaskIds = useMemo(() => {
    if (!activeProfile) return null;
    // Filhas de grupo ficam com o grupo: ele diz quantas o cliente precisa.
    const grouped = groups.map((group) => group.id);
    return visibleTasks
      .filter((task) => !task.disabled_at)
      .filter((task) => !task.group_id || grouped.indexOf(task.group_id) === -1)
      .filter((task) => taskMatchesProfile(task, activeProfile))
      .map((task) => task.id);
  }, [activeProfile, visibleTasks, groups]);

  // Grupos que valem para o cliente e não estão cobertos (nenhuma filha, ou
  // mais de uma quando o grupo pede só uma), contando o que está marcado agora.
  const pendingGroups = useMemo(() => groups.filter((group) => {
    const items = groupChildren(group, tasks.map((task) => (
      Object.assign({}, task, { is_assigned: selected.indexOf(task.id) > -1 })
    )));
    const applies = groupApplies(group, activeProfile);
    const state = groupStatus(group, items, applies).state;
    return items.length > 0 && (state === GROUP_PENDING || state === GROUP_OVER);
  }), [groups, tasks, selected, activeProfile]);

  const exceptions = useMemo(
    () => (profileTaskIds ? selected.filter((id) => profileTaskIds.indexOf(id) === -1) : []),
    [profileTaskIds, selected]
  );

  const missing = useMemo(
    () => (profileTaskIds ? profileTaskIds.filter((id) => selected.indexOf(id) === -1) : []),
    [profileTaskIds, selected]
  );

  const toggleTask = useCallback((taskId, checked) => {
    setSelected((previous) => {
      if (checked) {
        return previous.indexOf(taskId) > -1 ? previous : previous.concat([taskId]);
      }
      return previous.filter((id) => id !== taskId);
    });
  }, []);

  const discard = useCallback(() => {
    setSelected(baseline);
  }, [baseline]);

  const save = useCallback(async () => {
    if (!customerId || !dirty) return;

    setSaving(true);
    try {
      if (toAdd.length > 0) {
        await request('POST', `/customer/${customerId}/tasks`, { task_ids: toAdd }, false);
      }
      if (toRemove.length > 0) {
        await request('DELETE', `/customer/${customerId}/tasks`, { task_ids: toRemove }, false);
      }
      setBaseline(selected);
      setTasks((previous) => previous.map((task) => ({
        ...task,
        is_assigned: selected.indexOf(task.id) > -1,
      })));
      message.success('Tarefas do cliente atualizadas.');
      if (onChange) onChange();
    } catch (error) {
      // o interceptor da request ja avisa o usuario
    } finally {
      setSaving(false);
    }
  }, [customerId, dirty, toAdd, toRemove, selected, onChange]);

  const columns = useMemo(() => [
    {
      title: '',
      key: 'toggle',
      width: 60,
      render: (text, record) => (
        <Switch
          size="small"
          checked={selected.indexOf(record.id) > -1}
          onChange={(checked) => toggleTask(record.id, checked)}
        />
      ),
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const inProfile = profileTaskIds ? profileTaskIds.indexOf(record.id) > -1 : null;
        const isOn = selected.indexOf(record.id) > -1;

        return (
          <span>
            {text}
            {record.disabled_at && (
              <Tag color="red" style={{ marginLeft: 8 }}>desativada</Tag>
            )}
            {inProfile === false && isOn && (
              <Tooltip title="Marcada à mão: a regra deste cliente não sugere esta tarefa">
                <Tag color="blue" style={{ marginLeft: 8 }}>exceção</Tag>
              </Tooltip>
            )}
            {inProfile === true && !isOn && (
              <Tooltip title="A regra deste cliente sugere esta tarefa, e ela está desmarcada">
                <Tag color="orange" style={{ marginLeft: 8 }}>faltando</Tag>
              </Tooltip>
            )}
            {hasRule(record) && (
              <Tooltip title={`Regra da tarefa: ${describeRule(record)}`}>
                <Icon type="thunderbolt" style={{ marginLeft: 6, color: '#faad14' }} />
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: 'Departamento',
      key: 'department',
      width: 150,
      render: (text, record) => {
        const info = getDepartment(record.department);
        return <Tag color={info.color}>{info.name}</Tag>;
      },
      filters: Object.keys(DEPARTMENTS).map((id) => ({
        text: DEPARTMENTS[id].name,
        value: id,
      })),
      onFilter: (value, record) => String(record.department) === String(value),
    },
    {
      title: 'Quando?',
      key: 'frequency',
      width: 160,
      render: (text, record) => frequency(record.frequency, record),
    },
  ], [selected, toggleTask, profileTaskIds]);

  if (!customerId) {
    return (
      <Alert
        type="info"
        showIcon
        message="Nenhum cliente vinculado"
        description="Vincule o cliente e salve o ticket para escolher as obrigações que ele passa a cumprir."
      />
    );
  }

  if (loading) {
    return <Skeleton active paragraph={{ rows: 10 }} />;
  }

  return (
    <>
      <RuleBar data-rule-bar>
        <Icon type="thunderbolt" style={{ color: activeProfile ? '#faad14' : '#bfbfbf' }} />
        {activeProfile ? (
          <span style={{ flex: 1 }}>
            Regra do cliente: <strong>{activeProfile.label}</strong>
            {profileTaskIds && missing.length > 0 && (
              <Tag color="orange" style={{ marginLeft: 8 }}>{missing.length} faltando</Tag>
            )}
            {pendingGroups.length > 0 && (
              <Tooltip title={pendingGroups.map((group) => group.name).join(', ')}>
                <Tag color="orange" style={{ marginLeft: 4 }} data-pending-groups>
                  {pendingGroups.length === 1 ? '1 grupo pendente' : pendingGroups.length + ' grupos pendentes'}
                </Tag>
              </Tooltip>
            )}
            {profileTaskIds && exceptions.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 4 }}>{exceptions.length} exceção(ões)</Tag>
            )}
          </span>
        ) : (
          <span style={{ flex: 1 }}>Este cliente ainda não tem regra — sem ela, o sistema não sugere tarefas.</span>
        )}
        {onOpenRule && (
          <Button size="small" type={activeProfile ? 'default' : 'primary'} onClick={onOpenRule}>
            {activeProfile ? 'Ver regra' : 'Definir regra'}
          </Button>
        )}
      </RuleBar>

      <Toolbar>
        <Input
          placeholder="Buscar tarefas"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          style={{ width: 220 }}
          prefix={<Icon type="search" style={{ color: '#bfbfbf' }} />}
        />
        <Counters>
          <strong style={{ color: '#262626' }}>{selected.length}</strong> de {visibleTasks.length} obrigações marcadas
        </Counters>
      </Toolbar>

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={filteredTasks}
        pagination={false}
      />

      {dirty && (
        <PendingBar>
          <PendingText>
            {toAdd.length > 0 && (
              <span><Diff tone="in">+{toAdd.length}</Diff> a incluir</span>
            )}
            {toAdd.length > 0 && toRemove.length > 0 && <span> · </span>}
            {toRemove.length > 0 && (
              <span><Diff tone="out">-{toRemove.length}</Diff> a remover</span>
            )}
            <span> — nada foi gravado ainda.</span>
          </PendingText>
          <div>
            <Button size="small" onClick={discard} disabled={saving} style={{ marginRight: 8 }}>
              Desfazer
            </Button>
            <Button size="small" type="primary" onClick={save} loading={saving}>
              Salvar tarefas
            </Button>
          </div>
        </PendingBar>
      )}
    </>
  );
};

export default CustomerTasks;
